import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTooltip } from "context/TooltipProvider";
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";

import {
  errorHandlerOnForm,
  getAvatarBgColor,
  getInitials,
  hasRole,
} from "utils/helper";
import {
  AssetReferralActionStatusEnum,
  AssetReferralForwardingStatusEnum,
  LoginAccount,
  ProhibitionFormData,
  RecoveryFormData,
  Referral,
  ReferralFormData,
  Roles,
} from "types";
import { ProsecutorAssignmentSection } from "components/referralDetails/ProsecutorAssignment";
import { ReferralFormSection } from "components/referralDetails/ReferralFormSection";
import {
  createAssetReceptionMethod,
  createAssetReferral,
  fetchAssetReceptionMethodsList,
  fetchAssetReferralById,
  fetchProsecutors,
  updateAssetReferral,
} from "services/assetRecoveryService";
import FileViewer from "components/file-components/fileViewer2";
import { RecoverySection } from "components/assetRecovery/RecoverySection";
import { RecoveryCourtPopup } from "components/pop-up/RecoveryCoutPopup";
import RecoveryCourtSection from "components/referralDetails/RecoveryCourtSection";
import ProsecutorReassignPopup from "components/pop-up/ProsecutorReassignPopup";
import ReferralForwardPopup from "components/pop-up/ReferralForwardPopup";
import CreateReinitiateRequestPopup from "../Reinitiate/AddReinitiate";
import CreateSuspensionRequestPopup from "../Suspension/AddSuspensionRequest";
import ReinitiateContainer from "../Reinitiate/ReinitiateContainer";
import ActionsDropdown from "components/button/ActionsDropdown";
import SuspensionContainer from "../Suspension/SuspensionContainer";

const defaultFormData: ReferralFormData = {
  reception_method: "",
  letter_number: "",
  date: "",
  letter_info: "",
  referal_summary: "",
  letter_file: [],
};

const defaultProhibitionFormData: ProhibitionFormData = {
  prohabition_request_registration_number: null,
  prohabiton_request_date_registrar: null,
};
const defaultRecoveryFormData: RecoveryFormData = {
  asset_recovery_request_registration_number: null,
  asset_recovery_request_date_registrar: null,
};

interface AssetReferralRegisterProps {
  referralId: string | undefined;
  referral: Referral | null;
  onReferralCreated?: (newReferralId: string) => void;
  setReferralData?: React.Dispatch<React.SetStateAction<Referral | null>>;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
  handleCreateTerminationRequestClick?: () => void;
  handleAddAssetRecoveryDecisionClick?: () => void;
  handleAddAssetDecisionClick?: () => void;
}

const AssetReferralRegister: React.FC<AssetReferralRegisterProps> = ({
  referralId,
  referral,
  onReferralCreated,
  setReferralData,
  setActiveTab,
  onNewDataAdded,
  handleCreateTerminationRequestClick,
  handleAddAssetRecoveryDecisionClick,
  handleAddAssetDecisionClick,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [formState, setFormState] = useState<ReferralFormData>(defaultFormData);
  const [prohibitionFormData, setProhibitionFormData] =
    useState<ProhibitionFormData>(defaultProhibitionFormData);
  const [recoveryFormData, setRecoveryFormData] = useState<RecoveryFormData>(
    defaultRecoveryFormData,
  );
  const [methods, setMethods] = useState<{ uu_id: string; name: string }[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingRecovery, setIsEditingRecovery] = useState(false);
  const [showProhibitionForm, setShowProhibitionForm] = useState(false);
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);

  const [isRecoveryCourtPopupOpen, setIsRecoveryCourtPopupOpen] =
    useState(false);
  const [selectedProsecutor, setSelectedProsecutor] = useState("");
  const [prosecutors, setProsecutors] = useState<any[]>([]);
  const navigate = useNavigate();
  const { loginData } = useAuth();
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    name: string;
    size: number;
    url: string;
    isFetched: boolean;
    type: string;
    uuid: string;
  } | null>(null);

  const [isSuspensionRequestPopupOpen, setIsSuspensionRequestPopupOpen] =
    useState(false);
  const [isReinitiateRequestPopupOpen, setIsReinitiateRequestPopupOpen] =
    useState(false);
  // Role checks
  const isAssetCoordinator = hasRole(loginData?.roles, [
    Roles.assetCoordinator,
  ]);

  const isAssetRegistrar = hasRole(loginData?.roles, [Roles.registrar]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isProsecutorReassignPopupOpen, setIsProsecutorReassignPopupOpen] =
    useState(false);
  const [isForwardPopupOpen, setIsForwardPopupOpen] = useState(false);
  const fileRowProps = {
    fileViewerNumber: 2,
    setFileViewer2: setIsFileViewerOpen,
    setSelectedApplicantDocument: (document: any) => {
      setSelectedDocument(document);
      setIsFileViewerOpen(true);
    },
    // setIsUserDetailsOpen:setIsUserDetailsOpen(false)
  };
  // Computed values

  const hasRecoveryData =
    referral?.asset_recovery_request_registration_number ||
    referral?.asset_recovery_request_date_registrar;

  const hasCourtData = Boolean(
    referral?.prohabition_court_name ||
    referral?.prohabition_court_level ||
    referral?.prohabition_court_region ||
    referral?.prohabition_court_zone ||
    referral?.prohabition_court_woreda ||
    referral?.prohabition_request_first_hearing_date ||
    referral?.prohabition_request_court_registration_number,
  );
  const hasRecoveryCourtData = Boolean(
    referral?.asset_recovery_court_name ||
    referral?.asset_recovery_court_level ||
    referral?.asset_recovery_court_region ||
    referral?.asset_recovery_court_zone ||
    referral?.asset_recovery_court_woreda ||
    referral?.asset_recovery_first_hearing_date ||
    referral?.asset_recovery_request_court_registration_number,
  );

  const canAssignProsecutor =
    isEditMode &&
    isAssetCoordinator &&
    referral?.forward_status ===
      AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR &&
    referral?.action_status ===
      AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUEST_APPROVED;

  useEffect(() => {
    if (referralId && referral) {
      setIsEditMode(true);
      setFormState({
        reception_method: referral.reception_method || "",
        letter_number: referral.letter_number || "",
        date: referral.date || "",
        letter_info: referral.letter_info || "",
        reception_method_others: referral.reception_method || "",
        registerd_by: referral.registerd_by || null,
        admin_unit_id: referral.admin_unit_id || null,
        org_unit_id: referral.org_unit_id || null,
        action_status: referral.action_status || "Pending",
        receiving_office: referral.receiving_office || null,
        referal_summary: referral.referal_summary || "",
        prohabition_request_registration_number:
          referral?.prohabition_request_registration_number || "",
        letter_file: referral?.letter_files || [],
      });

      // Set prohibition form data
      setProhibitionFormData({
        prohabition_request_registration_number:
          referral.prohabition_request_registration_number || null,
        prohabiton_request_date_registrar:
          referral.prohabiton_request_date_registrar || null,
      });
      setRecoveryFormData({
        asset_recovery_request_registration_number:
          referral.asset_recovery_request_registration_number || null,
        asset_recovery_request_date_registrar:
          referral.asset_recovery_request_date_registrar || null,
      });

      // Show prohibition form if both fields are null and status is correct
      if (
        !referral.prohabition_request_registration_number &&
        !referral.prohabiton_request_date_registrar &&
        referral?.action_status ===
          AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED
      ) {
        setShowProhibitionForm(true);
      }
      if (
        !referral.asset_recovery_request_registration_number &&
        !referral.asset_recovery_request_date_registrar &&
        referral?.action_status ===
          AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUEST_APPROVED
      ) {
        setShowRecoveryForm(true);
      }
    } else {
      // Reset to create mode if no referralId or referral
      setIsEditMode(false);
      setFormState(defaultFormData);
      setProhibitionFormData(defaultProhibitionFormData);
    }
  }, [referralId, referral]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleRecoveryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRecoveryFormData((prevState) => ({
      ...prevState,
      [name]: value || null,
    }));
  };

  const validateForm = () => {
    if (!formState.reception_method)
      return t("reception_method_required", {
        defaultValue: "Reception Method is Required.",
      });
    if (
      formState.letter_info.toLowerCase() === "organization" &&
      !formState.letter_number
    )
      return t("letter_number_required", {
        defaultValue: "Letter Number is Required.",
      });
    if (!formState.date)
      return t("date_required", {
        defaultValue: "Date is Required.",
      });

    // if (!formState.letter_info) return "Letter Information is Required.";
    return "";
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();

    const userId = loginData?.user_id ?? null;
    const username = loginData?.full_name ?? null;
    const org_unit_id = loginData?.org_unit ?? null;
    const admin_unit_id = loginData?.admin_unit ?? null;
    console.log("Submitting referral form:", loginData);

    if (error) {
      showTooltip(error, "error");
      return;
    }

    try {
      // Handle custom reception method creation if needed
      if (formState.reception_method_others) {
        formState.reception_method = formState.reception_method_others;
        await createAssetReceptionMethod({
          name: formState.reception_method_others,
        });
      }

      // Only set these fields for new referrals, not when editing
      if (!isEditMode) {
        formState.registerd_by_user_id = userId?.toString();
        formState.org_unit_id = org_unit_id?.toString();
        formState.admin_unit_id = admin_unit_id?.toString();
        formState.registerd_by = username;
        formState.action_status = "Pending";
      }

      // Prepare form data for multipart upload if files exist
      let submissionData: any;

      if (uploadedFiles.length > 0) {
        // Create FormData for multipart upload
        const formData = new FormData();

        // Append all form fields
        Object.keys(formState).forEach((key) => {
          const value = formState[key as keyof ReferralFormData];
          if (value !== null && value !== undefined) {
            // Convert to string if it's not a file
            formData.append(key, value.toString());
          }
        });

        uploadedFiles.forEach((file) => {
          formData.append("letter_file", file);
        });
        submissionData = formData;
      } else {
        // No files, use regular JSON data
        submissionData = formState;
      }
      if (isEditMode && referralId) {
        // Update existing referral
        await updateAssetReferral(referralId, submissionData);
        showTooltip(t("successfully_updated"), "success");
        setIsEditing(false);
        setUploadedFiles([]);
      } else {
        // Create new referral
        const response = await createAssetReferral(submissionData);
        if (response && response.id) {
          setFormState(defaultFormData);
          setUploadedFiles([]);
          showTooltip(t("successfully_submitted"), "success");
          if (onReferralCreated) {
            onReferralCreated(response.id);
          } else {
            // Fallback: navigate directly if no callback provided
            navigate(`/asset-recovery/${response.id}`);
          }
        } else {
          console.error("No ID in response:", response);
          throw new Error("Failed to create referral: No ID returned");
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      showTooltip(
        errorHandlerOnForm(error as { message: string }) ||
          t("failed_to_update_or_create_referral", {
            defaultValue: `Failed to ${isEditMode ? "update" : "create"} referral`,
          }),
        "error",
      );
    }
  };

  const validateRecoveryForm = () => {
    if (!recoveryFormData.asset_recovery_request_registration_number)
      return t("asset_recovery_request_registration_number_required", {
        defaultValue: "Asset Recovery Request Registration Number is Required.",
      });
    if (!recoveryFormData.asset_recovery_request_date_registrar)
      return t("asset_recovery_request_date_required", {
        defaultValue: "Asset Recovery Request Date is Required.",
      });
    return "";
  };
  const handleRecoverySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateRecoveryForm();

    if (error) {
      showTooltip(error, "error");
      return;
    }

    if (!referralId) {
      showTooltip(
        t("referral_id_required", { defaultValue: "Referral ID is required" }),
        "error",
      );
      return;
    }

    try {
      // Create a copy of the form data to submit
      const dataToSubmit = { ...recoveryFormData };

      // Add action_status directly to the submission data
      if (referral?.application_type === "administrative") {
        dataToSubmit.action_status =
          AssetReferralActionStatusEnum?.ASSET_RECOVERY_FINAL_VERDICT;
      }

      const response = await updateAssetReferral(referralId, dataToSubmit);
      showTooltip(
        t("recovery_details_updated_successfully", {
          defaultValue: "Recovery details updated successfully!",
        }),
        "success",
      );
      setIsEditingRecovery(false);
      setShowRecoveryForm(false);

      // Refresh referral data
      if (setReferralData && referralId && response) {
        const updatedData = await fetchAssetReferralById(referralId);
        setReferralData(updatedData);
        if (onNewDataAdded && updatedData) {
          onNewDataAdded(updatedData);
          setActiveTab?.("Referral");
        }
      }
    } catch (error) {
      showTooltip(
        errorHandlerOnForm(error as { message: string }) ||
          t("failed_to_update_recovery_details", {
            defaultValue: "Failed to update recovery details",
          }),
        "error",
      );
    }
  };
  const handleForwardToCoordinator = async () => {
    try {
      const formData = new FormData();
      formData.append(
        "forward_status",
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR,
      );

      const response = await updateAssetReferral(
        referral?.id as string,
        formData,
      );
      showTooltip(t("successfully_forwarded"), "success");
      if (setReferralData && referralId && response) {
        const updatedData = await fetchAssetReferralById(referralId);
        setReferralData(updatedData);
        if (onNewDataAdded && updatedData) {
          onNewDataAdded(updatedData);
          setActiveTab?.("Referral");
        }
      }
    } catch (error) {
      console.error("Error forwarding to coordinator:", error);
      showTooltip(
        t("failed_to_forward_to_coordinator", {
          defaultValue: "Failed to forward to coordinator.",
        }),
        "error",
      );
    }
  };

  const handleAssignReAssign = async () => {
    if (!selectedProsecutor) {
      showTooltip(t("please_select_prosecutor"), "error");
      return;
    }

    try {
      const formData = new FormData();
      if (
        referral?.action_status ===
        AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED
      ) {
        formData.append(
          "forward_status",
          AssetReferralForwardingStatusEnum?.ASSIGNED_TO_PROSECUTOR,
        );
        formData.append(
          "action_status",
          AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION,
        );
      } else if (
        referral?.action_status ===
        AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUEST_APPROVED
      ) {
        formData.append(
          "forward_status",
          AssetReferralForwardingStatusEnum?.ASSIGNED_TO_PROSECUTOR,
        );
        formData.append(
          "action_status",
          AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_LITIGATION,
        );
      }

      // Only update assigned_prosecutor if a new one is selected
      if (
        selectedProsecutor &&
        selectedProsecutor !== referral?.assigned_prosecutor_user_id
      ) {
        // Find the selected prosecutor from the prosecutors list
        const selectedProsecutorData = prosecutors.find(
          (pros) => pros?.user?.id === selectedProsecutor,
        );

        if (selectedProsecutorData) {
          formData.append(
            "assigned_prosecutor",
            `${selectedProsecutorData.user?.first_name} ${selectedProsecutorData.user?.last_name}`,
          );
          formData.append("assigned_prosecutor_user_id", selectedProsecutor);
        }
      }

      const response = await updateAssetReferral(
        referral?.id as string,
        formData,
      );
      showTooltip(t("successfully_assigned"), "success");

      // Refresh referral data
      if (setReferralData && referralId && response) {
        const updatedData = await fetchAssetReferralById(referralId);
        setReferralData(updatedData);
        if (onNewDataAdded && updatedData) {
          onNewDataAdded(updatedData);
          setActiveTab?.("Referral");
        }
      }
    } catch (error) {
      console.error("Error assigning:", error);
      showTooltip(
        t("failed_to_assign_prosecutor", {
          defaultValue: "Failed to assign prosecutor.",
        }),
        "error",
      );
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleRecoveryEditClick = () => {
    setIsEditingRecovery(true);
    setShowRecoveryForm(true);
  };
  const handleCancelEdit = () => {
    if (referralId && referral) {
      // Reset form data from the passed referral prop
      setFormState({
        reception_method: referral.reception_method || "",
        letter_number: referral.letter_number || "",
        date: referral.date || "",
        letter_info: referral.letter_info || "",
        reception_method_others: referral.reception_method || "",
        registerd_by: referral.registerd_by || null,
        admin_unit_id: referral.admin_unit_id || null,
        org_unit_id: referral.org_unit_id || null,
        action_status: referral.action_status || "Pending",
        receiving_office: referral.receiving_office || null,
        referal_summary: referral.referal_summary || "",
        prohabition_request_registration_number:
          referral?.prohabition_request_registration_number || "",
        letter_file: referral?.letter_files || [],
      });
      setIsEditing(false);
    }
  };

  const handleCancelRecoveryEdit = () => {
    if (referralId && referral) {
      // Reset prohibition form data from the passed referral prop
      setRecoveryFormData({
        asset_recovery_request_registration_number:
          referral.asset_recovery_request_registration_number || null,
        asset_recovery_request_date_registrar:
          referral.asset_recovery_request_date_registrar || null,
      });
      setIsEditingRecovery(false);

      // Hide form if both fields are not null (data exists)
      if (
        referral.asset_recovery_request_registration_number ||
        referral.asset_recovery_request_date_registrar
      ) {
        setShowRecoveryForm(false);
      }
    }
  };

  const handleAddRecoveryDetails = () => {
    setShowRecoveryForm(true);
    setIsEditingRecovery(true);
  };
  let initials = "PR";
  let bgColor = "PR";
  useEffect(() => {
    fetchAssetReceptionMethodsList().then(setMethods).catch(console.error);
  }, []);

  // Fetch prosecutors
  useEffect(() => {
    if (
      isEditMode &&
      isAssetCoordinator &&
      referral?.action_status ===
        AssetReferralActionStatusEnum?.ASSET_RECOVERY_REQUEST_APPROVED &&
      referral?.forward_status ===
        AssetReferralForwardingStatusEnum?.FORWARDED_TO_COORDINATOR
    ) {
      fetchProsecutors(loginData as LoginAccount)
        .then(setProsecutors)
        .catch(console.error);
    }
  }, [
    isAssetCoordinator,
    isEditMode,
    referral?.action_status,
    referral?.forward_status,
  ]);

  // Set selectedProsecutor when referral data is loaded
  useEffect(() => {
    if (referralId && referral) {
      setIsEditMode(true);

      // Set prohibition form data
      setProhibitionFormData({
        prohabition_request_registration_number:
          referral.prohabition_request_registration_number || null,
        prohabiton_request_date_registrar:
          referral.prohabiton_request_date_registrar || null,
      });

      // Initialize selectedProsecutor with current assigned prosecutor user_id
      if (referral.assigned_prosecutor_user_id) {
        setSelectedProsecutor(referral.assigned_prosecutor_user_id);
        initials = getInitials(referral?.assigned_prosecutor);
        bgColor = getAvatarBgColor(referral?.assigned_prosecutor);
      }

      // Show prohibition form if both fields are null and status is correct
      if (
        !referral.prohabition_request_registration_number &&
        !referral.prohabiton_request_date_registrar &&
        referral?.action_status ===
          AssetReferralActionStatusEnum?.PROHIBITION_REQUEST_APPROVED
      ) {
        setShowProhibitionForm(true);
      }
    } else {
      // Reset to create mode if no referralId or referral
      setIsEditMode(false);
      setFormState(defaultFormData);
      setProhibitionFormData(defaultProhibitionFormData);
      setSelectedProsecutor(""); // Reset selected prosecutor
    }
  }, [referralId, referral]);

  return (
    <>
      <div className="ml-1 mr-4 space-y-6">
        {referral && (
          <div className="flex justify-end mb-2">
            <ActionsDropdown
              onReinitiateClick={setIsReinitiateRequestPopupOpen}
              onSuspensionClick={setIsSuspensionRequestPopupOpen}
              onForwardClick={setIsForwardPopupOpen}
              action_status={referral?.action_status as string}
              referral={referral}
              handleCreateTerminationRequestClick={
                handleCreateTerminationRequestClick
              }
              handleAddAssetRecoveryDecisionClick={
                handleAddAssetRecoveryDecisionClick
              }
              handleAddAssetDecisionClick={handleAddAssetDecisionClick}
            />
          </div>
        )}
        {referral?.action_status?.toLowerCase() === "reinitiate requested" &&
          isAssetCoordinator && (
            <ReinitiateContainer
              isAssetCoordinator={isAssetCoordinator}
              onReinitiateCreated={onNewDataAdded}
              referralId={referralId as string}
              action_status={referral?.action_status as string}
              referral={referral}
            />
          )}
        {referral?.action_status?.toLowerCase() === "suspension requested" &&
          isAssetCoordinator && (
            <SuspensionContainer
              isAssetCoordinator={isAssetCoordinator}
              onSuspensionCreated={onNewDataAdded}
              referralId={referralId as string}
              action_status={referral?.action_status as string}
              referral={referral}
            />
          )}
        {!hasRecoveryCourtData && (
          <RecoverySection
            isEditMode={isEditMode}
            isAssetRegistrar={isAssetRegistrar}
            referral={referral}
            recoveryFormData={recoveryFormData}
            isEditingRecovery={isEditingRecovery}
            showRecoveryForm={showRecoveryForm}
            hasRecoveryData={hasRecoveryData ? true : false}
            hasRecoveryCourtData={hasRecoveryCourtData ? true : false}
            onRecoveryChange={handleRecoveryChange}
            onRecoverySubmit={handleRecoverySubmit}
            onRecoveryEditClick={handleRecoveryEditClick}
            onCancelRecoveryEdit={handleCancelRecoveryEdit}
            onAddRecoveryDetails={handleAddRecoveryDetails}
            onForwardToCoordinator={handleForwardToCoordinator}
            onOpenCourtPopup={() => setIsRecoveryCourtPopupOpen(true)}
          />
        )}
        <ProsecutorAssignmentSection
          canAssignProsecutor={canAssignProsecutor}
          selectedProsecutor={selectedProsecutor}
          prosecutors={prosecutors}
          referral={referral}
          onProsecutorChange={setSelectedProsecutor}
          // onAssign={handleAssignReAssign}
          onAssignReAssign={handleAssignReAssign}
        />

        {hasRecoveryData && referral.application_type !== "administrative" && (
          <RecoveryCourtSection
            hasCourtData={hasRecoveryCourtData}
            referral={referral}
            // onForwardToCoordinator={handleForwardToCoordinator}
            onOpenCourtPopup={() => setIsRecoveryCourtPopupOpen(true)}
            isAssetRegistrar={isAssetRegistrar}
            isEditMode={isEditMode}
            isEditing={isEditing}
            onEditClick={() => setIsRecoveryCourtPopupOpen(true)}
          />
        )}

        <ReferralFormSection
          isEditMode={isEditMode}
          isEditing={isEditing}
          isAssetCoordinator={isAssetCoordinator}
          formState={formState}
          methods={methods}
          onChange={handleChange}
          onSubmit={handleSubmit}
          onEditClick={handleEditClick}
          onCancelEdit={handleCancelEdit}
          prohabition_request_registration_number={
            referral?.prohabition_request_registration_number || ""
          }
          asset_recovery_request_registration_number={
            referral?.asset_recovery_request_registration_number || ""
          }
          action_status={referral?.action_status as string}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          fileRowProps={fileRowProps}
          hasCourtData={hasCourtData}
          hasRecoveryCourtData={hasRecoveryCourtData}
          referral={referral}
          setIsProsecutorReassignPopupOpen={setIsProsecutorReassignPopupOpen}
        />
      </div>

      {/* <AssetReferralInvestigation
        referralId={referralId as string}
        referral={referral}
        investigationData={
          hasInvestigationData
            ? {
                investigation_summary: referral?.investigation_summary,
                investigation_summary_file:
                  referral?.investigation_summary_file,
                investigation_summary_file_details:
                  referral?.investigation_summary_files ?? undefined,
              }
            : undefined
        }
        setReferralData={setReferralData}
        onInvestigationCreated={onNewDataAdded}
        setActiveTab={setActiveTab}
        fileRowProps={fileRowProps}
      /> */}

      <RecoveryCourtPopup
        isOpen={isRecoveryCourtPopupOpen}
        onClose={() => setIsRecoveryCourtPopupOpen(false)}
        referralId={referralId as string}
        onSuccess={() => {
          if (referralId) {
            fetchAssetReferralById(referralId)
              .then((data) => setReferralData?.(data))
              .catch(() =>
                showTooltip(
                  t("failed_to_refresh_referral_data", {
                    defaultValue: "Failed to refresh referral data",
                  }),
                  "error",
                ),
              );
          }
        }}
      />
      <ProsecutorReassignPopup
        isOpen={isProsecutorReassignPopupOpen}
        onClose={() => setIsProsecutorReassignPopupOpen(false)}
        referral={referral}
        assignedProsecutor={referral?.assigned_prosecutor_user_id}
        onProsecutorUpdated={onNewDataAdded}
      />
      <ReferralForwardPopup
        isOpen={isForwardPopupOpen}
        onClose={() => setIsForwardPopupOpen(false)}
        referral={referral as Referral}
        onForwardSuccess={onNewDataAdded}
      />
      <CreateReinitiateRequestPopup
        isOpen={isReinitiateRequestPopupOpen}
        onClose={() => setIsReinitiateRequestPopupOpen(false)}
        onReinitiateCreated={onNewDataAdded}
        referral={referral as Referral}
        referralId={referralId as string}
      />
      <CreateSuspensionRequestPopup
        isOpen={isSuspensionRequestPopupOpen}
        onClose={() => setIsSuspensionRequestPopupOpen(false)}
        onSuspensionCreated={onNewDataAdded}
        referral={referral as Referral}
        referralId={referralId as string}
      />

      <FileViewer
        selectedApplicantDocument={selectedDocument || undefined}
        isPopupOpen={isFileViewerOpen}
        setIsPopupOpen={setIsFileViewerOpen}
      />
    </>
  );
};

export default AssetReferralRegister;
